(function () {
  const roots = document.querySelectorAll("[data-hebio-questionnaire]");
  if (!roots.length) return;

  const byId = (items) => Object.fromEntries(items.map((item) => [item.id, item]));
  const arrayValue = (value) => Array.isArray(value) ? value : value ? [value] : [];

  function parseJsonScript(root, selector, fallback) {
    const script = root.querySelector(selector);
    if (!script) return fallback;
    try {
      return JSON.parse(script.textContent || "");
    } catch (_error) {
      return fallback;
    }
  }

  function htmlEscape(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: window.themeVariables?.settings?.currencyCode || "EUR"
    }).format(value);
  }

  function addPoints(scores, points) {
    Object.entries(points || {}).forEach(([key, value]) => {
      scores[key] = (scores[key] || 0) + Number(value || 0);
    });
  }

  function isV2Config(config) {
    return String(config?.version || "").startsWith("2.") || config?.questions?.some((question) => question.id === "priorita");
  }

  function matchesAnswerExpression(expression, answers) {
    const inMatch = String(expression || "").match(/^([\w-]+) in \[([^\]]+)]$/);
    if (inMatch) return inMatch[2].split(",").map((value) => value.trim()).includes(answers[inMatch[1]]);
    const equalMatch = String(expression || "").match(/^([\w-]+)=([\w-]+)$/);
    return equalMatch ? answers[equalMatch[1]] === equalMatch[2] : false;
  }

  function matchesCondition(condition, answers, selectedAreas) {
    if (!condition) return true;
    if (condition.tipo === "area") return selectedAreas.has(condition.valore);
    if (condition.tipo === "sesso") return answers.sesso === condition.valore;
    if (condition.tipo === "risposta") return arrayValue(condition.valori).includes(answers[condition.domanda]);
    if (condition.area) return selectedAreas.has(condition.area);
    if (condition.risposta) return matchesAnswerExpression(condition.risposta, answers);
    if (condition.sesso) return answers.sesso === condition.sesso;
    if (condition.field) return answers[condition.field] === condition.equals;
    return true;
  }

  function getVisibleQuestions(config, answers) {
    const v2 = isV2Config(config);
    const selectedObjectives = new Set(arrayValue(v2 ? answers.priorita : answers.D2));
    return config.questions.filter((question) => {
      if (v2 && (question.showIf?.risposta || question.showIf?.tipo === "risposta")) {
        const implicitArea = question.id.startsWith("fem_") ? "femminile" : question.id.startsWith("met_") ? "metabolica" : null;
        if (implicitArea && !selectedObjectives.has(implicitArea)) return false;
      }
      if (question.showIfObjective && !selectedObjectives.has(question.showIfObjective)) return false;
      if (question.showIfAnyObjective && !question.showIfAnyObjective.some((objective) => selectedObjectives.has(objective))) return false;
      if (question.showIfAnyObjectiveOrAgeRange) {
        const objectiveList = question.showIfAnyObjectiveOrAgeRange.objectives || [];
        const ageRanges = question.showIfAnyObjectiveOrAgeRange.ageRanges || [];
        const hasObjective = objectiveList.some((objective) => selectedObjectives.has(objective));
        const hasAge = ageRanges.includes(answers.D5 || answers.ageRange);
        if (!hasObjective && !hasAge) return false;
      }
      if (question.showIf && !matchesCondition(question.showIf, answers, selectedObjectives)) return false;
      return true;
    });
  }

  function computeResult(config, answers, profile) {
    const scores = {};
    const tags = new Set();
    const products = config.products || {};
    const objectives = config.objectives || {};
    const rules = config.rules || {};
    const visible = getVisibleQuestions(config, { ...answers, gender: profile.gender });

    visible.forEach((question) => {
      const selected = new Set(arrayValue(answers[question.id]));
      if (question.id === "D2") {
        selected.forEach((objective) => addPoints(scores, { [objective]: 3 }));
      }
      (question.options || []).forEach((option) => {
        if (!selected.has(option.value)) return;
        addPoints(scores, option.points);
        (option.tags || []).forEach((tag) => tags.add(tag));
      });
    });

    if (profile.gender === "uomo" && ["55-64", "65-plus"].includes(profile.ageRange)) {
      addPoints(scores, { prostata: 1 });
      tags.add("genere:uomo");
    }

    if (profile.gender !== "donna") delete scores.donna;
    if (profile.gender !== "uomo") delete scores.prostata;

    const ranking = Object.entries(scores)
      .filter((entry) => entry[1] > 0)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map((entry) => entry[0]);

    if (!ranking.length) ranking.push("generale");

    const resolvePrimary = (objective) => {
      const obj = objectives[objective] || objectives.generale;
      for (const tag of tags) {
        if (obj.primaryByTag && obj.primaryByTag[tag]) return obj.primaryByTag[tag];
      }
      return obj.primaryDefault;
    };

    const selectedSkus = [];
    const first = ranking[0];
    const second = ranking[1];
    const magnesiumSku = rules.magnesiumComplement && rules.magnesiumComplement.addSku ? rules.magnesiumComplement.addSku : "MG8-01";
    const excludedSkus = rules.excludeSkus || ["LTTF-01", "COLEDIL-01"];
    selectedSkus.push(resolvePrimary(first));
    if (tags.has("sym:tensione-stress")) selectedSkus.push(magnesiumSku);
    else selectedSkus.push(...((objectives[first] && objectives[first].complementary) || []).slice(0, 1));
    if (second) selectedSkus.push(resolvePrimary(second));
    if (first === "intestino" && !selectedSkus.some((sku) => products[sku] && products[sku].margin)) selectedSkus.push("AGAPE-90");

    const recommendedSkus = [];
    selectedSkus.forEach((sku) => {
      if (!sku || recommendedSkus.includes(sku)) return;
      if (excludedSkus.includes(sku)) return;
      if (!products[sku]) return;
      recommendedSkus.push(sku);
    });

    const primary = objectives[first] || objectives.generale;
    const secondObjective = second ? objectives[second] : null;

    return {
      title: `Il profilo di ${profile.name || "te"}: ${primary.titleTheme}${secondObjective ? ` + ${secondObjective.titleTheme}` : ""}`,
      objectives: ranking.slice(0, 3).map((objective) => ({
        key: objective,
        label: objectives[objective] ? objectives[objective].label : objective,
        score: scores[objective] || 0
      })),
      scores,
      tags: Array.from(tags),
      recommendedSkus: recommendedSkus.slice(0, Number(rules.maxProducts || 3)),
      products: recommendedSkus.slice(0, Number(rules.maxProducts || 3)).map((sku) => products[sku]),
      expectation: primary.expectationShort,
      consultRequired: tags.has("flow:consulenza"),
      consultLabel: rules.consultLabel || "Consulenza online",
      safetyNote: tags.has("flow:consulenza") ? "In base alle risposte, la consulenza viene prima dei prodotti: le proposte sono da valutare con il medico." : null
    };
  }

  roots.forEach((root) => {
    const apiUrl = (root.dataset.apiUrl || "").replace(/\/$/, "");
    const source = root.dataset.source || "shopify-theme";
    const locale = root.dataset.locale || "it";
    const variantMap = parseJsonScript(root, "[data-variant-map]", {});
    const form = root.querySelector("[data-form]");
    const stepEl = root.querySelector("[data-step]");
    const resultEl = root.querySelector("[data-result]");
    const statusEl = root.querySelector("[data-status]");
    const prevButton = root.querySelector("[data-prev]");
    const nextButton = root.querySelector("[data-next]");
    const progressLabel = root.querySelector("[data-progress-label]");
    const progressBar = root.querySelector("[data-progress-bar]");
    const consentsEl = root.querySelector("[data-consents]");
    const answers = {};
    let config = null;
    let visibleQuestions = [];
    let currentIndex = 0;
    let lastResult = null;

    function setStatus(message, isError) {
      statusEl.hidden = !message;
      statusEl.textContent = message || "";
      statusEl.style.borderColor = isError ? "rgba(160, 35, 35, 0.45)" : "";
    }

    function profileFromAnswers() {
      if (isV2Config(config)) {
        return {
          name: answers.nome || "",
          email: answers.email || "",
          ageRange: answers.eta || "",
          gender: answers.sesso || ""
        };
      }
      return {
        name: answers.D3 || "",
        email: answers.D4 || "",
        ageRange: answers.D5 || "",
        gender: answers.D6 || ""
      };
    }

    function variantIdForSku(sku) {
      return variantMap[sku] || "";
    }

    function applyProductData(card, data) {
      if (!data) return;
      const media = card.querySelector("[data-product-media]");
      const title = card.querySelector("[data-product-title]");
      const meta = card.querySelector("[data-product-meta]");
      const link = card.querySelector("[data-product-link]");

      if (data.image && media) {
        media.innerHTML = `<img src="${htmlEscape(data.image)}" alt="${htmlEscape(data.title || title.textContent)}" loading="lazy">`;
        media.removeAttribute("aria-hidden");
      }

      if (title && data.title) title.textContent = data.title;

      if (meta && typeof data.price !== "undefined") {
        const availability = data.available === false ? "Non disponibile" : "Disponibile";
        meta.textContent = `${formatMoney(data.price)} · ${availability}`;
      }

      if (link && data.url) {
        link.href = data.url;
        link.hidden = false;
      }
    }

    function syncVisible() {
      visibleQuestions = getVisibleQuestions(config, { ...answers, ...profileFromAnswers() });
      if (currentIndex >= visibleQuestions.length) currentIndex = visibleQuestions.length - 1;
      if (currentIndex < 0) currentIndex = 0;
    }

    function renderQuestion() {
      syncVisible();
      const question = visibleQuestions[currentIndex];
      const phase = (config.phases || []).find((item) => item.id === question.phase);
      const total = visibleQuestions.length + 1;
      const progress = Math.max(8, Math.round(((currentIndex + 1) / total) * 100));

      const phaseNumber = isV2Config(config) ? Number(question.phase) + 1 : question.phase;
      progressLabel.textContent = `Fase ${phaseNumber} di 7${phase?.title ? ` - ${phase.title}` : ""}`;
      progressBar.style.width = `${progress}%`;
      prevButton.hidden = currentIndex === 0;
      consentsEl.hidden = isV2Config(config) ? question.id !== "email" : question.phase !== 3;
      nextButton.textContent = currentIndex === visibleQuestions.length - 1 ? "Genera profilo" : "Continua";

      const selected = arrayValue(answers[question.id]);
      let body = "";

      if (question.type === "text" || question.type === "email") {
        body = `
          <label class="hebio-questionnaire__field">
            <span class="sr-only">${htmlEscape(question.title)}</span>
            <input class="hebio-questionnaire__input" name="${question.id}" type="${question.type}" value="${htmlEscape(answers[question.id] || "")}" ${question.required ? "required" : ""}>
          </label>
        `;
      } else {
        const inputType = question.type === "multi" ? "checkbox" : "radio";
        const selectedAreas = new Set(arrayValue(answers.priorita));
        const availableOptions = (question.options || []).filter((option) => matchesCondition(option.showIf, answers, selectedAreas));
        body = `<div class="hebio-questionnaire__grid">` + availableOptions.map((option) => {
          const checked = selected.includes(option.value) ? "checked" : "";
          return `
            <label class="hebio-questionnaire__option">
              <input type="${inputType}" name="${question.id}" value="${htmlEscape(option.value)}" ${checked} ${question.required ? "required" : ""} data-exclusive="${option.exclusive ? "true" : "false"}">
              <span>${htmlEscape(answers.sesso === "uomo" && option.labelMale ? option.labelMale : answers.sesso === "donna" && option.labelFemale ? option.labelFemale : option.label)}</span>
            </label>
          `;
        }).join("") + `</div>`;
      }

      stepEl.innerHTML = `
        <p class="hebio-questionnaire__phase">${phase ? htmlEscape(phase.title) : `Fase ${question.phase}`}</p>
        <h2 class="hebio-questionnaire__question-title">${htmlEscape(question.title)}</h2>
        ${body}
        ${question.max ? `<p class="hebio-questionnaire__hint">Puoi selezionare fino a ${question.max} risposte.</p>` : ""}
      `;
    }

    function collectCurrent() {
      const question = visibleQuestions[currentIndex];
      const controls = Array.from(form.elements).filter((element) => element.name === question.id);

      if (question.type === "text" || question.type === "email") {
        const input = controls[0];
        if (!input.checkValidity()) {
          input.reportValidity();
          return false;
        }
        answers[question.id] = input.value.trim();
        return true;
      }

      const selected = controls.filter((input) => input.checked).map((input) => input.value);
      if (question.required && !selected.length) {
        setStatus("Seleziona una risposta per continuare.", true);
        return false;
      }
      if (question.max && selected.length > question.max) {
        setStatus(`Puoi selezionare al massimo ${question.max} risposte.`, true);
        return false;
      }
      answers[question.id] = question.type === "multi" ? selected : selected[0];
      setStatus("");
      return true;
    }

    async function submitProfile() {
      const requiredConsent = form.elements.consent_profile;
      if (!requiredConsent.checked) {
        requiredConsent.reportValidity();
        return;
      }

      nextButton.setAttribute("aria-busy", "true");
      nextButton.disabled = true;
      const profile = profileFromAnswers();
      const localResult = isV2Config(config) ? null : computeResult(config, answers, profile);
      if (localResult) {
        lastResult = localResult;
        renderResult(localResult, true);
      } else {
        renderPending();
      }

      try {
        if (!apiUrl) throw new Error("API URL mancante");
        const response = await fetch(`${apiUrl}/api/submissions`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            source,
            locale,
            profile,
            consents: {
              profile: form.elements.consent_profile.checked,
              marketing: form.elements.consent_marketing.checked,
              profiling: form.elements.consent_profiling.checked
            },
            answers
          })
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || "Errore salvataggio");
        lastResult = payload.result || localResult;
        if (!lastResult) throw new Error("Risultato API mancante");
        renderResult(lastResult, false);
      } catch (error) {
        if (localResult) {
          setStatus("Profilo generato, ma il salvataggio remoto non è riuscito. Controlla URL API e CORS.", true);
        } else {
          resultEl.hidden = true;
          form.hidden = false;
          renderQuestion();
          setStatus("Non è stato possibile generare il profilo. Controlla la connessione e riprova.", true);
        }
      } finally {
        nextButton.removeAttribute("aria-busy");
        nextButton.disabled = false;
      }
    }

    function renderPending() {
      form.hidden = true;
      resultEl.hidden = false;
      progressBar.style.width = "100%";
      resultEl.innerHTML = `
        <div class="hebio-questionnaire__result-head" aria-live="polite">
          <h2>Stiamo preparando il tuo profilo</h2>
          <p>Elaboriamo le risposte e selezioniamo gli obiettivi più rilevanti.</p>
        </div>
      `;
    }

    function renderResult(result, pending) {
      form.hidden = true;
      resultEl.hidden = false;
      progressLabel.textContent = "Fase 7 di 7 - Il tuo profilo";
      progressBar.style.width = "100%";

      const productHtml = (result.products || []).map((product) => {
        const variantId = variantIdForSku(product.sku);
        return `
        <div class="hebio-questionnaire__product" data-product-card data-sku="${htmlEscape(product.sku)}" data-variant-id="${htmlEscape(variantId || "")}">
          <div class="hebio-questionnaire__product-media" data-product-media aria-hidden="true"></div>
          <div class="hebio-questionnaire__product-info">
            <strong data-product-title>${htmlEscape(product.name)}</strong>
            <span class="hebio-questionnaire__sku">${htmlEscape(product.sku)}</span>
            <span class="hebio-questionnaire__product-meta" data-product-meta></span>
            <a class="hebio-questionnaire__product-link" data-product-link href="#" hidden>Vedi prodotto</a>
          </div>
        </div>
      `;
      }).join("");

      const scoreInfo = (score) => {
        const value = Math.max(0, Math.min(10, Number(score) || 0));
        if (value >= 8) return { value, label: "Criticità alta", className: "is-high" };
        if (value >= 4) return { value, label: "Criticità media", className: "is-medium" };
        return { value, label: "Criticità bassa", className: "is-low" };
      };
      const objectiveHtml = (result.objectives || []).map((objective) => {
        const score = scoreInfo(objective.score);
        return `
          <div class="hebio-questionnaire__score-card ${score.className}">
            <div class="hebio-questionnaire__score-head">
              <strong>${htmlEscape(objective.label)}</strong>
              <span>${htmlEscape(score.value)}/10</span>
            </div>
            <div
              class="hebio-questionnaire__score-track"
              role="img"
              aria-label="${htmlEscape(objective.label)}: ${htmlEscape(score.value)} su 10, ${htmlEscape(score.label)}"
              style="--hebio-score: ${score.value * 10}%"
            >
              <span class="hebio-questionnaire__score-marker" aria-hidden="true"></span>
            </div>
            <div class="hebio-questionnaire__score-state">${htmlEscape(score.label)}</div>
          </div>
        `;
      }).join("");

      const missingVariantSkus = (result.recommendedSkus || []).filter((sku) => !variantIdForSku(sku));
      const canCart = (result.recommendedSkus || []).length && !missingVariantSkus.length;

      resultEl.innerHTML = `
        <div class="hebio-questionnaire__result-head">
          <h2>${htmlEscape(result.title)}</h2>
          <p>${htmlEscape(result.expectation || "")}</p>
          ${pending ? `<p class="hebio-questionnaire__hint">Sto salvando il profilo e preparando l'invio email.</p>` : ""}
        </div>
        ${result.consultRequired ? `<div class="hebio-questionnaire__notice"><strong>${htmlEscape(result.consultLabel)}</strong><br>${htmlEscape(result.safetyNote)}${result.consultUrl ? `<br><a href="${htmlEscape(result.consultUrl)}">Vai alle consulenze</a>` : ""}</div>` : ""}
        <section class="hebio-questionnaire__scores" aria-labelledby="hebio-score-title">
          <div class="hebio-questionnaire__scores-heading">
            <div>
              <h3 id="hebio-score-title">Il tuo indice di criticità</h3>
              <p>La posizione indica quanto quest'area richiede attenzione in base alle tue risposte.</p>
            </div>
            <div class="hebio-questionnaire__score-legend" aria-label="Legenda indice di criticità">
              <span class="is-low"><i aria-hidden="true"></i>0–3 Bassa</span>
              <span class="is-medium"><i aria-hidden="true"></i>4–7 Media</span>
              <span class="is-high"><i aria-hidden="true"></i>8–10 Alta</span>
            </div>
          </div>
          <div class="hebio-questionnaire__pill-row">${objectiveHtml}</div>
        </section>
        <h3>Prodotti pensati per te</h3>
        <div class="hebio-questionnaire__product-grid">${productHtml}</div>
        ${missingVariantSkus.length ? `<p class="hebio-questionnaire__hint">Mancano i variant ID per questi SKU: ${missingVariantSkus.map(htmlEscape).join(", ")}.</p>` : ""}
        <div class="hebio-questionnaire__cart-actions">
          <button class="button" type="button" data-add-recommended ${canCart ? "" : "disabled"}>Aggiungi consigliati al carrello</button>
          <button class="button button--outline" type="button" data-restart>Rifai il questionario</button>
        </div>
      `;

      enrichProductCards();
    }

    async function enrichProductCards() {
      const cards = Array.from(resultEl.querySelectorAll("[data-product-card]"));
      if (!apiUrl || !cards.length) return;

      const skus = cards.map((card) => card.dataset.sku).filter(Boolean);
      const variants = skus
        .map((sku) => variantIdForSku(sku) ? `${sku}:${variantIdForSku(sku)}` : "")
        .filter(Boolean);
      const params = new URLSearchParams({
        skus: skus.join(","),
        variants: variants.join(",")
      });

      try {
        const response = await fetch(`${apiUrl}/api/products?${params.toString()}`);
        if (!response.ok) return;
        const payload = await response.json();
        cards.forEach((card) => applyProductData(card, payload.products?.[card.dataset.sku]));
      } catch (_error) {
        // Product details are optional enrichment; the recommendation itself stays visible.
      }
    }

    async function addRecommendedToCart() {
      if (!lastResult) return;
      const items = (lastResult.recommendedSkus || []).map((sku) => ({
        id: Number(variantIdForSku(sku)),
        quantity: 1,
        properties: {
          "_questionario_hebio": lastResult.title,
          "_sku_consigliato": sku
        }
      })).filter((item) => item.id);

      if (!items.length) return;
      const button = resultEl.querySelector("[data-add-recommended]");
      button.setAttribute("aria-busy", "true");
      button.disabled = true;

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ items })
      });

      button.removeAttribute("aria-busy");
      if (response.ok) window.location.href = `${Shopify.routes.root}cart`;
      else {
        button.disabled = false;
        setStatus("Non sono riuscito ad aggiungere i prodotti al carrello.", true);
      }
    }

    form.addEventListener("change", (event) => {
      const input = event.target;
      if (!input.matches("input[type='checkbox'][data-exclusive]")) return;
      const question = visibleQuestions[currentIndex];
      const controls = Array.from(form.elements).filter((element) => element.name === question.id);
      if (input.dataset.exclusive === "true" && input.checked) {
        controls.forEach((control) => {
          if (control !== input) control.checked = false;
        });
      } else if (input.checked) {
        controls.forEach((control) => {
          if (control.dataset.exclusive === "true") control.checked = false;
        });
      }
    });

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!collectCurrent()) return;
      if (currentIndex < visibleQuestions.length - 1) {
        currentIndex += 1;
        renderQuestion();
      } else {
        await submitProfile();
      }
    });

    prevButton.addEventListener("click", () => {
      if (currentIndex > 0) currentIndex -= 1;
      renderQuestion();
    });

    resultEl.addEventListener("click", (event) => {
      if (event.target.matches("[data-add-recommended]")) addRecommendedToCart();
      if (event.target.matches("[data-restart]")) window.location.reload();
    });

    async function boot() {
      try {
        if (!apiUrl) throw new Error("API URL mancante");
        const response = await fetch(`${apiUrl}/api/config`);
        config = await response.json();
        if (!response.ok || !config.questions) throw new Error("Configurazione API non valida");
        renderQuestion();
      } catch (error) {
        setStatus("Questionario non disponibile: controlla URL API Railway, deploy app e ALLOWED_ORIGINS.", true);
        form.hidden = true;
      }
    }

    boot();
  });
})();
